
import React, { useMemo } from 'react';
import { MemberModel, ScheduleEvent } from '../../../../models';
import { BarChart3, TrendingUp, Target, BookOpen, Clock, AlertCircle } from 'lucide-react';

interface StudentStatisticsProps {
    member: MemberModel;
    schedules: ScheduleEvent[];
}

const StudentStatistics: React.FC<StudentStatisticsProps> = ({ member, schedules }) => {
    const stats = useMemo(() => {
        const memberSchedules = schedules.filter(s => s.memberId === member.id);
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];
        const monthStr = todayStr.substring(0, 7);

        const getPeriodStats = (filteredSchedules: ScheduleEvent[]) => {
            const total = filteredSchedules.length;
            const completed = filteredSchedules.filter(s => s.status === 'completed').length;
            const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
            return { total, completed, rate };
        };

        const totalStats = getPeriodStats(memberSchedules);

        // Helper to get date string from timestamp or string
        const getDateStr = (dateVal: number | string) => {
            if (typeof dateVal === 'number') {
                return new Date(dateVal).toISOString().split('T')[0];
            }
            return dateVal; // If it's already string (legacy support)
        };

        const monthlyStats = getPeriodStats(memberSchedules.filter(s => getDateStr(s.date).startsWith(monthStr)));
        const dailyStats = getPeriodStats(memberSchedules.filter(s => getDateStr(s.date) === todayStr));

        const subjectData: Record<string, { total: number; completed: number }> = {};
        memberSchedules.forEach(s => {
            if (!subjectData[s.subject]) subjectData[s.subject] = { total: 0, completed: 0 };
            subjectData[s.subject].total++;
            if (s.status === 'completed') subjectData[s.subject].completed++;
        });

        return { totalStats, monthlyStats, dailyStats, subjectData };
    }, [member, schedules]);

    const ProgressCard = ({ title, rate, icon: Icon, colorClass }: any) => (
        <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/40 relative overflow-hidden group hover:border-indigo-100 transition-all">
            <div className={`absolute top-0 right-0 w-32 h-32 ${colorClass.replace('text-', 'bg-')} opacity-5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform`}></div>
            <div className="flex flex-col gap-4 relative z-10">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-white border border-slate-50 shadow-sm ${colorClass}`}>
                        <Icon size={20} />
                    </div>
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{title} 달성률</span>
                </div>
                <div className="flex items-end justify-between gap-4">
                    <span className="text-3xl font-black text-slate-800">{rate}%</span>
                    <div className="flex-1 max-w-[100px] h-2 bg-slate-100 rounded-full overflow-hidden mb-1.5">
                        <div className={`h-full ${colorClass.replace('text-', 'bg-')} rounded-full transition-all duration-1000`} style={{ width: `${rate}%` }}></div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 pb-20 sm:pb-0">
            {/* Header */}
            <div className="flex items-center justify-between bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <BarChart3 size={14} />
                        </div>
                        <h2 className="text-xl font-black text-slate-800">학습 통계</h2>
                    </div>
                    <p className="text-xs font-medium text-slate-400">회원님의 학습 달성도를 시각적으로 보여줍니다.</p>
                </div>
            </div>

            {/* Top Period Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <ProgressCard title="전체" rate={stats.totalStats.rate} icon={Target} colorClass="text-indigo-600" />
                <ProgressCard title="이달의" rate={stats.monthlyStats.rate} icon={TrendingUp} colorClass="text-emerald-600" />
                <ProgressCard title="오늘의" rate={stats.dailyStats.rate} icon={Clock} colorClass="text-amber-500" />
            </div>

            {/* Subject Progress Section */}
            <div className="bg-white p-6 sm:p-10 rounded-[3rem] border border-slate-100 shadow-xl shadow-slate-200/30">
                <h4 className="font-black text-slate-800 flex items-center gap-3 mb-8 text-lg">
                    <BookOpen size={22} className="text-indigo-600 uppercase" /> 과목별 이수 현황
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {Object.entries(stats.subjectData).map(([subject, data]) => {
                        const sRate = Math.round((data.completed / data.total) * 100);
                        return (
                            <div key={subject} className="group">
                                <div className="flex justify-between text-[11px] font-black text-slate-500 mb-2 uppercase tracking-widest">
                                    <span>{subject}</span>
                                    <span className="text-slate-800">{sRate}% <span className="text-slate-300 ml-1 font-bold">({data.completed}/{data.total})</span></span>
                                </div>
                                <div className="h-3 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-50">
                                    <div
                                        className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-1000 group-hover:brightness-110"
                                        style={{ width: `${sRate}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                    {Object.keys(stats.subjectData).length === 0 && (
                        <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-100 rounded-[2.5rem] bg-slate-50/50">
                            <AlertCircle size={40} className="text-slate-200 mx-auto mb-3" />
                            <p className="text-sm font-bold text-slate-400">학습 데이터가 충분하지 않습니다.</p>
                        </div>
                    )}
                </div>
            </div>


        </div>
    );
};

export default StudentStatistics;
